import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Tooltip, IconButton, Icon } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation, Link } from 'react-router-dom';

import Loader from '../../common/loader';
import Crud from '../../common/crud';
import NotFound from '../../common/not-found';
import ViewTitle from '../../common/view-title';
import Trash from '../../common/trash';

import AuthContext from '../../context';
import { del, get, post, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    tableContainer: {
        width: '100%'
    }
}));

const Categories = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [state, setState] = useState({
        loading: true,
        categories: [],
        category: {
            idcategory: 0,
            code: '',
            name: ''
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Categories';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/categories');
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, categories: response?.data ?? [] }));
        } else {
            console.log('[Categories] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, category: { ...prev.category, [target.name]: target.value } }));

    const validateForm = (type) => {
        const { idcategory, code, name } = state.category;

        switch (type) {
            case 'update':
                const data = state.categories.filter(value => value.idcategory === idcategory);

                if (data[0]) {
                    const before = JSON.stringify(data[0]);
                    const after = JSON.stringify(state.category);
                    if (before !== after) {
                        if (code.trim() && name.trim()) {
                            return true;
                        } else {
                            showToast('All fields is required', 'error');
                            return false;
                        }
                    } else {
                        showToast('Update some field', 'error');
                        return false;
                    }
                } else {
                    showToast('Select some category', 'error');
                    return false;
                }

            case 'store':

                if (code.trim() && name.trim()) {
                    return true;
                } else {
                    showToast('All fields is required', 'error');
                    return false;
                }

            case 'delete':
                if (idcategory === 0) {
                    showToast('Select some category');
                    return false;
                }
                const cond = window.confirm('Are you sure you want to delete this category?');

                return Boolean(idcategory && cond);
            default:
                break;
        }
    }

    const clear = () => {
        setState(prev => ({ ...prev, category: { ...prev.category, idcategory: 0, code: '', name: '' } }));
    }

    const store = async () => {
        const cond = validateForm('store');

        if (cond) {
            showLoader(true);
            const response = await post('/category/store', state.category);
            if (response.status === 200) {
                setState(prev => ({ ...prev, categories: response?.data ?? prev.categories }));
                showToast(response.message);
            } else {
                console.log('[Categories] store error', response);
                helpers.failRequest(response, showToast, signOut);
                showLoader(false);

                return false;
            }
            showLoader(false);
        }

        return cond;
    }

    const update = async () => {
        const { idcategory } = state.category;
        const cond = validateForm('update');

        if (cond) {
            showLoader(true);
            const response = await put(`/category/${idcategory}/update`, state.category);
            if (response.status === 200) {
                setState(prev => ({ ...prev, categories: response?.data ?? prev.categories }));
                showToast(response.message);
            } else {
                console.log('[Categories] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const { idcategory } = state.category;
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/category/${idcategory}`);
            if (response.status === 200) {
                setState(prev => ({ ...prev, categories: response?.data ?? prev.categories }));
                showToast(response.message);
            } else {
                console.log('[Categories] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const renderCell = ({ row }) => {
        return (
            <Link to={`/history/categories/${row.idcategory}`} target='_blank'>
                <Tooltip title='Show history' placement='top'>
                    <IconButton>
                        <Icon>{'history'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'code', headerName: 'Code', flex: 1 },
        { field: 'name', headerName: 'Name', flex: 2 },
        { field: 'actions', headerName: 'Actions', flex: 0.5, renderCell }
    ];

    if (state.loading) {
        return <Loader />;
    }
    if (!state.permissions.read_attr) {
        return <NotFound />;
    }

    return (
        <div className={classes.root}>
            <ViewTitle title='Categories' />
            <div>
                <Grid container>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormLabel>{'Code'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Code'
                                    name='code'
                                    value={state.category.code}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormLabel>{'Name'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Name'
                                    name='name'
                                    value={state.category.name}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                </Grid>
            </div>

            <Crud
                permissions={state.permissions}
                create={{ create: clear, store }}
                update={update}
                destroy={destroy}
                cancel={clear}
            />

            {
                state.permissions.delete_attr &&
                <Trash table='categories' id='idcategory' onRefresh={makeRequest} />
            }

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.categories}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idcategory}
                    loading={state.loading}
                    onRowClick={({ row }) => setState(prev => ({ ...prev, category: row }))}
                />
            </div>
        </div>
    );
}

export default Categories;